import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";

import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { EstimateCostDatabaseItemsGridNew as EstimateCostDatabaseItems } from "@Features/estimation";
import { useRef, useState } from "react";
import type { ComponentRef } from "react";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import { itemListSources } from "@Actions/estimation";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { contractWBSController } from "@Features/contracts/state/contracts.actions";
import _ from "lodash";
import type { RootState } from "@Reducers/index";
import type {
  ContractWBSControllerRequest,
  ContractWBSControllerResponse,
} from "@ServerTypes/contracts/contracts.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

interface ChangeOrderState {
  isEnabled: boolean;
}

/**
 * An item produced by the `getAllFamilyOfEstimateItems` WBS controller action.
 * Matches a single entry of `ContractWBSControllerResponse["result"]`.
 */
type WbsEstimateFamilyItem = NonNullable<ContractWBSControllerResponse["result"]>[number];

/**
 * The `selectedItemsArray` element accepted by the `getAllFamilyOfEstimateItems`
 * WBS controller request. Used to type both the grid rows and the action payload.
 */
type SelectedItemPayload = Extract<
  ContractWBSControllerRequest,
  { action: "getAllFamilyOfEstimateItems" }
>["selectedItemsArray"][number];

/** The BOQ grid's imperative handle (`TreeGridRef`), taken from the component rather than restated. */
type BoqGridHandle = NonNullable<ComponentRef<typeof EstimateCostDatabaseItems>>;

/**
 * A row returned by the linked-BOQ grid's `dataView.getAllSelectedItems()`, taken from the grid's
 * own handle. `quantityLinked` is projected onto contract BOQ rows by contracts.service.ts but is
 * not part of the grid's row type, so it is the one field added here.
 */
type SelectedBoqRow = NonNullable<ReturnType<BoqGridHandle["dataView"]["getAllSelectedItems"]>[number]> & {
  quantityLinked?: number;
};

/** One ancestor entry of the request's `parentsList`: a 24-hex id or the "0" root sentinel. */
type RequestParent = NonNullable<SelectedItemPayload["parentsList"]>[number];

/**
 * The request wants a 24-hex ancestor id or the "0" root sentinel, but the grid row types every
 * ancestor `itemId` as an optional plain string. Ancestors that are neither are dropped rather than
 * sent, since the service feeds each one straight to `new ObjectId()`.
 *
 * The callback's return is annotated because its two branches build differently-typed literals
 * (`{itemId: "0"}` and `{itemId: ObjectIdString}`); without it `flatMap` infers its element type
 * from whichever branch it sees first and rejects the other.
 */
const toRequestParentsList = (parentsList: SelectedBoqRow["parentsList"]): SelectedItemPayload["parentsList"] =>
  parentsList?.flatMap((parent): RequestParent[] => {
    if (parent.itemId === "0") {
      return [{ itemId: "0" }];
    }
    const parsed = objectIdString.safeParse(parent.itemId);
    return parsed.success ? [{ itemId: parsed.data }] : [];
  });

/**
 * Draft BOQ item row built from a `getAllFamilyOfEstimateItems` result entry and
 * handed off (untyped) to `addItemsCallBack`. Field types mirror the source
 * `WbsEstimateFamilyItem`; numeric quantity/amount fields can be `null` for parent
 * (group) rows. Mutated in a second pass to assign `serial` and change-order fields.
 */
interface LinkedBoqItemDraft {
  description: WbsEstimateFamilyItem["description"];
  HSN_SAC: WbsEstimateFamilyItem["HSN_SAC"];
  hasChild: WbsEstimateFamilyItem["hasChild"];
  itemLevel: WbsEstimateFamilyItem["itemLevel"];
  parentsList: WbsEstimateFamilyItem["parentsList"];
  type: string;
  code: WbsEstimateFamilyItem["code"];
  estimateId: WbsEstimateFamilyItem["estimateId"];
  estimateVersionId: WbsEstimateFamilyItem["estimateVersionId"];
  rateAnalysis: WbsEstimateFamilyItem["rateAnalysis"];
  itemId: WbsEstimateFamilyItem["itemId"];
  measurementUnit: WbsEstimateFamilyItem["measurementUnit"] | null;
  rate: WbsEstimateFamilyItem["rate"];
  purchaseUnit: WbsEstimateFamilyItem["purchaseUnit"] | null;
  parentId: WbsEstimateFamilyItem["parentId"];
  quantity: number | null;
  availableQuantityInParentEstimate: number;
  amount: number | null;
  availableQuantity: number | null;
  revisedQuantity: number | null;
  revisedAmount: number | null;
  changeOrderQuantity?: number | null;
  changeOrderAmount?: number | null;
  isAddedThroughChangeOrder?: boolean;
  serial?: number;
}

interface LinkBoqItemsProps {
  contractId?: ObjectIdString;
  hideBoqItemsModal: () => void;
  source: string;
  showBackBtn?: boolean;
  backBtnCallback?: () => void;
  addItemsCallBack: (data: unknown) => void;
  wbsOriginalItemId?: string;
  changeOrderState?: ChangeOrderState;
  showBtnLoader?: boolean;
}

function LinkBoqItems({
  contractId,
  hideBoqItemsModal,
  source,
  showBackBtn = false,
  backBtnCallback,
  addItemsCallBack,
  wbsOriginalItemId,
  changeOrderState,
  showBtnLoader = false,
}: LinkBoqItemsProps) {
  const { t } = useTranslation();
  /** Per-item custom quantities keyed by BOQ item id, collected from the child grid. */
  const customQRef = useRef<Record<string, number>>({});
  /** The grid handle `EstimateCostDatabaseItems` forwards; only its `dataView` is read here. */
  const boqItemsRef = useRef<ComponentRef<typeof EstimateCostDatabaseItems>>(null);
  const dispatch = useDispatch();

  const [linkBoqItemBtnState, setLinkBoqItemBtnState] = useState(false);
  const [autoPopulateState, setAutoPopulateState] = useState(false);

  const contractDetails = useSelector((state: RootState) => state.contractDetails);
  const currencyDetails = useSelector((state: RootState) => state.currencyDetails);

  const setBtnState = (state: boolean) => {
    setLinkBoqItemBtnState(state);
  };

  const handleLinkBoqItemsClick = async () => {
    // `getAllSelectedItems()` is declared to return rows, but the dataView pushes `getItemById(id)`
    // for every id still selected, and that is `undefined` for a selection kept across a refresh or
    // a filter change; the grid's own placeholder rows are dropped alongside those holes.
    const selectedRows: Array<SelectedBoqRow | undefined> = boqItemsRef.current!.dataView.getAllSelectedItems();
    const selectedBoqData = selectedRows.filter(
      (record): record is SelectedBoqRow => record != null && !record.__isDummy,
    );

    // Last match wins, as before. `content` is set in exactly the cases the old
    // `isAlreadyAddedInThisContract` flag tracked, so the flag itself was redundant — and the
    // `isAlreadyLinkedWithThisWBSItem` local it was OR-ed with was a `const false` that no branch
    // ever wrote to (both branches below wrote the other flag), i.e. permanently dead.
    let content: TranslatedString | undefined;
    for (const data of selectedBoqData) {
      if (data.isAlreadyAddedInThisContract) {
        content = t("One of the selected Item(s) is already added in the contract");
      }
      if (data.isAlreadyLinkedWithThisWBSItem) {
        content = t("One of the selected Item(s) is already linked with this wbs item");
      }
    }

    if (content) {
      dispatch(
        showToastNotification({
          content,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (source == "contract") {
      const boqItems = selectedBoqData.map((data) => {
        const quantity = customQRef.current[data.itemId] ?? data.quantityLinked ?? 0;
        return {
          customQuantity: quantity,
          itemId: data.itemId,
        };
      });
      const data = {
        action: "link",
        entity: "boqItems",
        contractId,
        parentContractId: contractDetails.parentId,
        data: boqItems,
        autoPopulateAllAvailableQuantity: autoPopulateState,
      };
      addItemsCallBack(data);
    } else if (source == "WBSView" || source == "BoqView") {
      const selectedItemsArray = selectedBoqData.map((item) => ({
        itemId: item.itemId,
        parentsList: toRequestParentsList(item.parentsList),
      }));

      const res = await contractWBSController({
        selectedItemsArray,
        action: "getAllFamilyOfEstimateItems",
        estimateVersionId: _.get(contractDetails, "linkedEstimateVersion.versionId"),
        estimateId: _.get(contractDetails, "linkedEstimateVersion.estimateId"),
        contractId: contractId!,
      });

      if (res.data.status) {
        const dataSource = (res.data.result ?? []).map((item) => {
          const totalQuantity =
            (item.manualQuantity ?? 0) + (item.quantityFromTakeoffs ?? 0) - (item.quantityUsedInThisContract ?? 0);
          const customQuantity = customQRef.current[item.itemId!] || 0;
          const quantityValue = item.hasChild ? null : customQuantity || (autoPopulateState ? totalQuantity : 0);
          const amountValue = item.hasChild
            ? null
            : customQuantity
              ? customQuantity * item.rate!
              : autoPopulateState
                ? totalQuantity * item.rate!
                : 0;
          const data: LinkedBoqItemDraft = {
            description: item.description,
            HSN_SAC: item.HSN_SAC,
            hasChild: item.hasChild,
            itemLevel: item.itemLevel,
            parentsList: item.parentsList,
            type: "ESTIMATE",
            code: item.code,
            estimateId: item.estimateId,
            estimateVersionId: item.estimateVersionId,
            rateAnalysis: item.rateAnalysis,
            itemId: item.itemId,
            measurementUnit: item.hasChild ? null : item.measurementUnit,
            rate: item.hasChild ? null : item.rate,
            purchaseUnit: item.hasChild ? null : item.purchaseUnit,
            parentId: item.parentId == "0" ? null : item.parentId,
            quantity: quantityValue,
            availableQuantityInParentEstimate:
              totalQuantity - (customQuantity || (autoPopulateState ? totalQuantity : 0)),
            amount: amountValue,
            availableQuantity: quantityValue,
            revisedQuantity: quantityValue,
            revisedAmount: amountValue,
          };
          if (changeOrderState!.isEnabled) {
            data.quantity = 0;
            data.amount = 0;
            data.changeOrderQuantity = quantityValue;
            data.changeOrderAmount = amountValue;
            data.isAddedThroughChangeOrder = true;
          } else {
            data.changeOrderQuantity = 0;
            data.changeOrderAmount = 0;
          }
          return data;
        });

        const childSerialMapper: Record<string, number> = {};
        const newDatasource = dataSource.map((item) => {
          if (item.parentId) {
            const parentSerial = childSerialMapper[item.parentId];
            if (parentSerial) {
              item.serial = parentSerial + 1;
              childSerialMapper[item.parentId] = parentSerial + 1;
            } else {
              item.serial = 1;
              childSerialMapper[item.parentId] = 1;
            }
          }
          return item;
        });
        addItemsCallBack(newDatasource);
      }
    }
  };

  const getCustomBoqQuantity = ({ itemId, value }: { itemId: string; value: number }) => {
    customQRef.current = { ...customQRef.current, [itemId]: value };
  };

  const boqModelContent = () =>
    currencyDetails.currencyDetail ? (
      <EstimateCostDatabaseItems
        contractId={contractId}
        contractVersionId={_.get(contractDetails, "versionId")}
        estimateVersionId={_.get(contractDetails, "linkedEstimateVersion.versionId")}
        estimateId={_.get(contractDetails, "linkedEstimateVersion.estimateId")}
        getCustomBoqQuantity={getCustomBoqQuantity}
        ref={boqItemsRef}
        setBtnState={setBtnState}
        enableCheckboxSelector
        source={itemListSources.LINK_BOQ_ITEMS}
        currencyData={{
          id: currencyDetails.currencyDetail.id,
          selectedSymbol: { name: currencyDetails.symbolDetail?.name ?? "" },
        }}
        childParentMapper={{
          itemId: "itemId",
          parentId: "parentId",
        }}
        setAutoPopulateState={setAutoPopulateState}
        wbsOriginalItemId={wbsOriginalItemId}
      />
    ) : undefined;

  function BoqFooterContent() {
    return (
      <div className=" flex gap-4">
        <ButtonComponent
          disabled={!linkBoqItemBtnState}
          loading={showBtnLoader}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleLinkBoqItemsClick}
        >
          {["WBSView", "BoqView"].includes(source) ? t("Add") : t("Link")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={hideBoqItemsModal}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
  }
  const boqHeaderContent = () => (
    <div className="  w-full flex justify-between rounded-md">
      <div className="flex items-center gap-2">
        {showBackBtn && (
          <IconComponent
            className="cursor-pointer"
            name="BackwardArrow"
            width="1rem"
            height="1rem"
            onClick={backBtnCallback}
          />
        )}
        {t("BOQ Items")}
      </div>
    </div>
  );

  return (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={BoqFooterContent()}
      width="80%"
      height={"80%"}
      headerTitle={boqHeaderContent()}
      onCancel={hideBoqItemsModal}
      isVisible
      modelContent={boqModelContent()}
    />
  );
}
export default LinkBoqItems;
